import React, { useMemo } from 'react';
import { getIn, useFormikContext } from 'formik';

import { Text } from '~/components/core';
import FsTooltip from '~/components/core/FsWrappers/FsTooltip/FsTooltip';
import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';

import { moiEmailScopeKey } from './moiEmailScope';
import type { MoiEmailTemplate, MoiEmailTemplateFormValues, MoiMethod } from './types';

export const MOI_METHODS_FIELD_ID = 'applies_to_moi_method_keys';

interface MoiEmailMethodsMultiSelectFormikProps {
  methods: MoiMethod[];
  // Other templates (excluding the one being edited), enabled and disabled alike. A method is
  // disabled only when it is mapped on a non-Default one that shares this form's targeting scope —
  // the backend keys mappings by scope (ignoring is_enabled), so a differently-scoped template
  // mapping the same method is not a conflict, but a same-scope disabled one still is.
  otherTemplates: MoiEmailTemplate[];
  disabled?: boolean;
  optionsLoading?: boolean;
  // True while the template's target (sub-orgs / LOBs / coverages / countries / states) is not fully
  // selected. Method conflicts are keyed by the full scope, so picking methods before the target is
  // set would let the user select methods already mapped under the scope they end up choosing.
  targetIncomplete?: boolean;
}

const MoiEmailMethodsMultiSelectFormik: React.FC<MoiEmailMethodsMultiSelectFormikProps> = ({
  methods,
  otherTemplates,
  disabled = false,
  optionsLoading = false,
  targetIncomplete = false,
}) => {
  const { values, setFieldValue } = useFormikContext<MoiEmailTemplateFormValues>();
  const selectedKeys = (getIn(values, MOI_METHODS_FIELD_ID) as string[] | undefined) ?? [];

  // key -> name of the other active, same-scope template it is currently mapped to (drives disable +
  // tooltip). The Default carries no mappings and is excluded by construction. A stale selection
  // under a changed target can't occur: MoiEmailTargetGuard erases the selection (with confirmation)
  // before any target input can change.
  const currentScopeKey = moiEmailScopeKey(values);
  const mappedElsewhereByKey = useMemo(() => {
    const dict: Record<string, string> = {};
    otherTemplates.forEach((template) => {
      if (template.is_default) return;
      if (moiEmailScopeKey(template) !== currentScopeKey) return;
      (template.moi_methods ?? []).forEach((method) => {
        dict[method.key] = template.template_name ?? '';
      });
    });
    return dict;
  }, [otherTemplates, currentScopeKey]);

  const displayNameByKey = useMemo(() => {
    const dict: Record<string, string> = {};
    methods.forEach((method) => {
      dict[method.key] = method.display_name;
    });
    return dict;
  }, [methods]);

  // Sort a copy by display name (MultiSelectField's `sortAlphabetic` would mutate this array in place).
  const options = useMemo(
    () =>
      methods
        .map((method) => method.key)
        .sort((a, b) => (displayNameByKey[a] ?? a).localeCompare(displayNameByKey[b] ?? b)),
    [methods, displayNameByKey]
  );
  const disabledOptions = useMemo(() => Object.keys(mappedElsewhereByKey), [mappedElsewhereByKey]);

  return (
    <div>
      <Text variant={Text.VARIANTS.SM} className="mb-8">
        Applies to MOI methods
      </Text>
      {/* A disabled MUI input swallows hover events, so the tooltip wraps the field in a plain div. */}
      <FsTooltip
        title={
          targetIncomplete
            ? 'Fill in the template target above (sub-organizations, lines of business, coverages and states) to unlock the MOI methods selection'
            : ''
        }
      >
        <div>
          <MultiSelectField<string>
            id={MOI_METHODS_FIELD_ID}
            value={selectedKeys}
            options={options}
            fullWidth
            disabled={disabled || targetIncomplete}
            helperText={targetIncomplete ? 'Select the template target first' : undefined}
            disabledOptions={disabledOptions}
            optionsLoading={optionsLoading}
            // Mapping a method is an explicit choice — never auto-select the sole remaining option.
            autoSelectSingleOption={false}
            onChange={(newValues) => setFieldValue(MOI_METHODS_FIELD_ID, newValues)}
            renderValue={(selected) => selected.map((key) => displayNameByKey[key] ?? key).join(', ')}
            renderOption={(key) => {
              const label = displayNameByKey[key] ?? key;
              const mappedTo = mappedElsewhereByKey[key];
              return (
                <FsTooltip title={mappedTo ? `Currently mapped to template '${mappedTo}'` : ''}>
                  <span>{label}</span>
                </FsTooltip>
              );
            }}
          />
        </div>
      </FsTooltip>
    </div>
  );
};

export default MoiEmailMethodsMultiSelectFormik;
